<template>
  <div class="starfield" aria-hidden="true">
    <div class="stars stars-small"></div>
    <div class="stars stars-medium"></div>
    <div class="stars stars-large"></div>
    <div class="shooting-star"></div>
    <div class="shooting-star delay"></div>

    <!-- there's a dead bird here. don't ask why. -->
    <div class="dead-bird" title="rip">
      <span class="orbit-star star-a">✦</span>
      <span class="orbit-star star-b">✧</span>
      <span class="orbit-star star-c">✦</span>
      <svg viewBox="0 0 32 32" width="30" height="30">
        <ellipse cx="16" cy="20" rx="10" ry="6" fill="var(--text-muted)" />
        <path
          d="M6 20 L1 24 M6 20 L1 17"
          stroke="var(--text-muted)"
          stroke-width="1.5"
          fill="none"
        />
        <path d="M12 26 L10 30 M12 26 L14 30" stroke="var(--text-muted)" stroke-width="1.5" />
        <path d="M20 26 L18 30 M20 26 L22 30" stroke="var(--text-muted)" stroke-width="1.5" />
        <circle cx="24" cy="16" r="4.5" fill="var(--text-muted)" />
        <path
          d="M27 15 L26 16 L27 17"
          stroke="var(--background-color)"
          stroke-width="1"
          fill="none"
        />
        <path
          d="M22.5 14.5 L23.7 15.7 M23.7 14.5 L22.5 15.7"
          stroke="var(--background-color)"
          stroke-width="1"
        />
        <path
          d="M25.3 14.5 L26.5 15.7 M26.5 14.5 L25.3 15.7"
          stroke="var(--background-color)"
          stroke-width="1"
        />
      </svg>
    </div>
  </div>
</template>

<script setup lang="ts"></script>

<style scoped>
  .starfield {
    position: fixed;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
    background: radial-gradient(
      ellipse at 50% -10%,
      var(--background-color-alt) 0%,
      var(--background-color) 60%
    );
  }

  .stars {
    position: absolute;
    inset: 0;
    background-repeat: repeat;
    opacity: var(--star-opacity);
  }

  .stars-small {
    background-image:
      radial-gradient(1px 1px at 20px 30px, var(--star-color), transparent),
      radial-gradient(1px 1px at 90px 120px, #fff, transparent),
      radial-gradient(1px 1px at 160px 60px, var(--primary-color), transparent),
      radial-gradient(1px 1px at 230px 180px, #fff, transparent),
      radial-gradient(1px 1px at 300px 40px, var(--secondary-color), transparent);
    background-size: 340px 220px;
    animation: twinkle 4s ease-in-out infinite;
  }

  .stars-medium {
    background-image:
      radial-gradient(1.5px 1.5px at 60px 90px, #fff, transparent),
      radial-gradient(1.5px 1.5px at 220px 20px, var(--primary-color), transparent),
      radial-gradient(1.5px 1.5px at 130px 200px, var(--star-color), transparent);
    background-size: 260px 260px;
    animation: twinkle 6s ease-in-out infinite reverse;
  }

  .stars-large {
    background-image:
      radial-gradient(2px 2px at 40px 160px, var(--secondary-color), transparent),
      radial-gradient(2px 2px at 180px 80px, #fff, transparent);
    background-size: 300px 300px;
    animation: twinkle 8s ease-in-out infinite;
  }

  @keyframes twinkle {
    0%,
    100% {
      opacity: calc(var(--star-opacity) * 0.6);
    }
    50% {
      opacity: var(--star-opacity);
    }
  }

  .shooting-star {
    position: absolute;
    top: 10%;
    left: -10%;
    width: 3px;
    height: 3px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 0 6px 1px #fff;
    opacity: var(--star-opacity);
    animation: shoot 7s linear infinite;
  }

  .shooting-star::before {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    width: 90px;
    height: 1px;
    background: linear-gradient(90deg, transparent, #fff);
    transform: translateY(-50%);
  }

  .shooting-star.delay {
    top: 35%;
    animation-delay: 3.5s;
  }

  @keyframes shoot {
    0% {
      transform: translate(0, 0);
      opacity: 0;
    }
    5% {
      opacity: var(--star-opacity);
    }
    25% {
      transform: translate(140vw, 90vh);
      opacity: 0;
    }
    100% {
      transform: translate(140vw, 90vh);
      opacity: 0;
    }
  }

  .dead-bird {
    position: absolute;
    bottom: 6%;
    right: 4%;
    opacity: 0.55;
    animation: drift 9s ease-in-out infinite;
  }

  @keyframes drift {
    0%,
    100% {
      transform: translateY(0) rotate(-3deg);
    }
    50% {
      transform: translateY(-6px) rotate(3deg);
    }
  }

  .orbit-star {
    position: absolute;
    top: -10px;
    left: 8px;
    font-size: 0.6rem;
    color: var(--star-color);
    animation: orbit 3s linear infinite;
  }

  .star-b {
    animation-delay: -1s;
  }

  .star-c {
    animation-delay: -2s;
  }

  @keyframes orbit {
    from {
      transform: rotate(0deg) translateX(14px) rotate(0deg);
    }
    to {
      transform: rotate(360deg) translateX(14px) rotate(-360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .stars,
    .shooting-star,
    .dead-bird,
    .orbit-star {
      animation: none;
    }
  }
</style>
